import { ProductAttributes, StandardProduct } from "../../hooks/useStandardProducts";

type ScalarArray = Array<string | number | boolean | undefined>;

export const ATTRIBUTES = {
  COVER_TYPE: "coverType",
  COVER_FINISH: "coverFinish",
  TEXT_STOCK: "textStock",
  STOCK_STYLE: "stockStyle",
  DIMENSIONS: "dimensions",
  ORIENTATION: "orientation",
  BIND_TYPE: "bindType",
  TEXT_PRINT_MODE: "textPrintMode",
  PRODUCT_CLASS: "productClass",
} as const;

export function isSoftcover(attributes: ProductAttributes) {
  const softcoverTypes: ScalarArray = ["SCIW"];
  return softcoverTypes.indexOf(attributes[ATTRIBUTES.COVER_TYPE]) >= 0;
}

export function isHardcover(attributes: ProductAttributes) {
  const hardcoverTypes: ScalarArray = ["HCIW"];
  return hardcoverTypes.indexOf(attributes[ATTRIBUTES.COVER_TYPE]) >= 0;
}

// Order disabled combinations after enabled ones, preserving relative order otherwise.
export function sortDisabledLast<T>(combinations: T[], isDisabled: (combination: T) => boolean): T[] {
  return [...combinations].sort((a, b) => {
    const aDisabled = isDisabled(a);
    const bDisabled = isDisabled(b);
    if (aDisabled === bDisabled) return 0;
    return aDisabled ? 1 : -1;
  });
}

/**
 * The steps the user would have to change to make an unavailable target reachable — a minimum edit distance over
 * their selections, counted in steps: one unit per step, since a step is a single user action even when it owns
 * several attributes (changing Paper Type is one edit, not two). Among the products that offer the target, we take
 * those the fewest steps away from the user's selections and union the steps they would change, then drop any later
 * step whose change is forced by an earlier named one (e.g. a size that exists in only one orientation). Returns the
 * restricting steps in the order they were passed; values compare as strings. See the
 * ing-937-restricting-option-tooltips design doc for the full rationale.
 */
export function getRestrictingOptions<Step extends { attributeKeys: string[] }>({
  standardProducts,
  currentSelections,
  targetAttributes,
  targetValues,
  candidateSteps,
}: {
  standardProducts: StandardProduct[];
  currentSelections: ProductAttributes;
  targetAttributes: string[];
  targetValues: ProductAttributes;
  candidateSteps: Step[];
}): Step[] {
  const targetKeys = new Set(targetAttributes);

  // Each candidate step reduced to the attributes we compare on: its own, minus the target's, minus any the user
  // hasn't selected. Steps left with nothing to compare drop out.
  const candidates = candidateSteps
    .map((step) => ({
      step,
      keys: step.attributeKeys.filter((key) => !targetKeys.has(key) && currentSelections[key] !== undefined),
    }))
    .filter((candidate) => candidate.keys.length > 0);

  const targetProducts = standardProducts.filter((product) =>
    targetAttributes.every((attr) => String(product.attributes[attr]) === String(targetValues[attr]))
  );
  if (targetProducts.length === 0) return [];

  // Edit distance in steps: the candidate steps a target product would have the user change (a step "changes" when
  // any of its compared attributes differ). Take the products the fewest steps away and union the steps they change.
  const changeSets = targetProducts.map((product) =>
    candidates.filter(({ keys }) => !matchesSelection(product, keys, currentSelections))
  );
  const minSize = Math.min(...changeSets.map((set) => set.length));
  const nearest = new Set(changeSets.filter((set) => set.length === minSize).flat());
  const named = candidates.filter((candidate) => nearest.has(candidate));

  // A named step is redundant when its value implies an earlier named step's value: changing that earlier step
  // then forces this one to change too, so we needn't list it. Keep only steps that don't imply an earlier named
  // step — which keeps the earliest of a mutually-implying group (e.g. orientation/size) and tells the user how
  // far back to go.
  return named
    .filter(
      (candidate, index) =>
        !named
          .slice(0, index)
          .some((earlier) =>
            laterValueImpliesEarlierValue(standardProducts, currentSelections, candidate.keys, earlier.keys)
          )
    )
    .map((candidate) => candidate.step);
}

// Whether a product agrees with the user's selections on every one of `keys` (values compared as strings).
function matchesSelection(product: StandardProduct, keys: string[], selections: ProductAttributes): boolean {
  return keys.every((key) => String(product.attributes[key]) === String(selections[key]));
}

// Whether the user's value for the later step implies their value for the earlier step across the catalog: every
// product that carries the later step's value also carries the earlier step's. When this holds, the later step
// cannot keep its value once the earlier step changes — changing the earlier step forces the later one — so the
// later step is a dependent consequence, and a tooltip that already names the earlier step needn't name it too.
function laterValueImpliesEarlierValue(
  standardProducts: StandardProduct[],
  selections: ProductAttributes,
  laterKeys: string[],
  earlierKeys: string[]
): boolean {
  let sawLater = false;
  for (const product of standardProducts) {
    if (!matchesSelection(product, laterKeys, selections)) continue;
    sawLater = true;
    if (!matchesSelection(product, earlierKeys, selections)) return false;
  }
  return sawLater;
}

/**
 * The unavailable-option sentence for a disabled value, e.g. "This bleed is not available in the size and paper
 * type you've selected." Joins the restrictor labels with an Oxford "and"; returns undefined for an empty list.
 * Labels are used verbatim — the caller passes each step's lowercase `tooltipLabel`.
 */
export function formatRestrictingStepsSentence(
  currentStepLabel: string,
  restrictingStepLabels: string[]
): string | undefined {
  if (restrictingStepLabels.length === 0) return undefined;
  const list = joinWithOxfordAnd(restrictingStepLabels);
  // \u2019 is the typographic apostrophe, matching the Figma copy and the tooltip test assertions.
  return `This ${currentStepLabel} is not available in the ${list} you\u2019ve selected.`;
}

function joinWithOxfordAnd(items: string[]): string {
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}
